import axios from "axios";
import { getFreshAccessToken } from "../chat/api";
import {
  emitSubscriptionRequired,
  isSubscriptionRequiredError,
} from "../../events/subscriptionEvents";

const rawApiOrigin = (
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  ""
).replace(/\/+$/, "");

export const API_BASE = rawApiOrigin
  ? rawApiOrigin.endsWith("/api/v1")
    ? rawApiOrigin
    : `${rawApiOrigin}/api/v1`
  : "/api/v1";

export const UNIVERSAL_EXTRACT_PATH = "/readpdf/universal-extract/";

const extractorApi = axios.create({ baseURL: API_BASE });

extractorApi.interceptors.request.use(async (config) => {
  const token = await getFreshAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

extractorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isSubscriptionRequiredError(error)) {
      emitSubscriptionRequired();
    }
    return Promise.reject(error);
  }
);

// ── Document Ingestion ──────────────────────────────────────────────────────
export const ingestDocument = async (file, { engine, reindex } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  if (engine) params.set("engine", engine);
  if (reindex) params.set("reindex", "true");
  const qs = params.toString();
  const res = await extractorApi.post(
    `/readpdf/document-ingest/${qs ? `?${qs}` : ""}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data; // { pages: [...], pdf_key, file_url }
};

// ── Template CRUD ───────────────────────────────────────────────────────────
export const getTemplates = async () => {
  const res = await extractorApi.get("/readpdf/extractor-templates/");
  return res.data;
};

// ── Billing ─────────────────────────────────────────────────────────────────
// All paths sit under /readpdf/billing/. Only plans/ is public; the rest need
// the bearer header the request interceptor above already attaches.
export const getBillingBalance = async () => {
  const res = await extractorApi.get("/readpdf/billing/balance/");
  return res.data;
};

export const getBillingPlans = async () => {
  const res = await extractorApi.get("/readpdf/billing/plans/");
  return res.data; // { plans: [...], addons: [...] }
};

export const getBillingSubscription = async () => {
  const res = await extractorApi.get("/readpdf/billing/subscription/");
  return res.data;
};

export const getBillingTransactions = async ({ limit = 50, offset = 0, kind } = {}) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (kind) params.set("kind", kind);
  const res = await extractorApi.get(`/readpdf/billing/transactions/?${params.toString()}`);
  return res.data; // { total, limit, offset, transactions: [...] }
};

// success_url / cancel_url are required — the backend has no configured
// fallback, so omitting them is a 400.
// Extractor Studio's billing lives in Settings > Billing, on its own tab.
export const STUDIO_BILLING_PATH = "/account/billing?tab=extractor-studio";
const billingReturnUrl = (suffix = "") => `${window.location.origin}${STUDIO_BILLING_PATH}${suffix}`;

export const createPlanCheckout = async (planKey, { successUrl, cancelUrl } = {}) => {
  const res = await extractorApi.post("/readpdf/billing/checkout/", {
    plan_key: planKey,
    success_url: successUrl || billingReturnUrl("&billing=success"),
    cancel_url: cancelUrl || billingReturnUrl("&billing=cancelled"),
  });
  return res.data; // { session_id, checkout_url, plan_key }
};

export const createAddonCheckout = async (addonKey, { successUrl, cancelUrl } = {}) => {
  const res = await extractorApi.post("/readpdf/billing/addon-checkout/", {
    addon_key: addonKey,
    success_url: successUrl || billingReturnUrl("&billing=success"),
    cancel_url: cancelUrl || billingReturnUrl("&billing=cancelled"),
  });
  return res.data; // { session_id, checkout_url, addon_key, credits }
};

export const createBillingPortalSession = async (returnUrl) => {
  const res = await extractorApi.post("/readpdf/billing/portal/", {
    return_url: returnUrl || billingReturnUrl(),
  });
  return res.data; // { session_id, portal_url }
};

export const changeBillingPlan = async (targetPlanKey) => {
  const res = await extractorApi.post("/readpdf/billing/change-plan/", { target_plan: targetPlanKey });
  return res.data; // shape differs for immediate vs scheduled — check `scheduled`
};

export const cancelBillingSubscription = async () => {
  const res = await extractorApi.post("/readpdf/billing/cancel/", {});
  return res.data; // { subscription_id, cancel_at_period_end, period_end }
};

export const getTemplate = async (id) => {
  const res = await extractorApi.get(`/readpdf/extractor-templates/${id}/`);
  return res.data;
};

export const createTemplate = async (payload) => {
  // payload: { name, slug, document_type, fields: [...] }
  const res = await extractorApi.post("/readpdf/extractor-templates/", payload);
  return res.data;
};

export const updateTemplate = async (id, payload) => {
  const res = await extractorApi.put(
    `/readpdf/extractor-templates/${id}/`,
    payload
  );
  return res.data;
};

// Partial update — use for isolated settings (slug, page range) so it never
// overwrites other fields the user may have edited but not yet saved.
export const patchTemplate = async (id, payload) => {
  const res = await extractorApi.patch(
    `/readpdf/extractor-templates/${id}/`,
    payload
  );
  return res.data;
};

export const deleteTemplate = async (id) => {
  await extractorApi.delete(`/readpdf/extractor-templates/${id}/`);
};

export const duplicateTemplate = async (id) => {
  const res = await extractorApi.post(
    `/readpdf/extractor-templates/${id}/duplicate/`
  );
  return res.data;
};

// ── Fields ──────────────────────────────────────────────────────────────────
export const getFields = async (templateId) => {
  const res = await extractorApi.get(
    `/readpdf/extractor-fields/?template=${templateId}`
  );
  return res.data;
};

export const createField = async (field) => {
  const res = await extractorApi.post("/readpdf/extractor-fields/", field);
  return res.data;
};

export const updateField = async (id, field) => {
  const res = await extractorApi.put(`/readpdf/extractor-fields/${id}/`, field);
  return res.data;
};

export const deleteField = async (id) => {
  await extractorApi.delete(`/readpdf/extractor-fields/${id}/`);
};

// ── Universal Extraction ────────────────────────────────────────────────────
export const universalExtract = async (file, templateSlug, { reindex } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams({ template: templateSlug });
  if (reindex) params.set("reindex", "true");
  const res = await extractorApi.post(
    `${UNIVERSAL_EXTRACT_PATH}?${params.toString()}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data;
};

// ── Legacy test extraction (sandbox) ───────────────────────────────────────
export const testExtraction = async (file, fieldsConfig) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("fields_config", JSON.stringify(fieldsConfig));
  const res = await extractorApi.post("/readpdf/extractor-test/", formData);
  return res.data;
};

// ── Async universal extraction (v2) — draft mode ────────────────────────────
// Submits a Builder draft's current fields for extraction against `file` as a
// background job instead of blocking the request thread. Backend picks the
// draft's own engine/model (DraftExtractionConfig), same as the old inline
// attach-sample path, but off the request thread and via the same async job
// pipeline every other v2 consumer (Make.com, etc.) already uses.
export const submitDraftExtraction = async (draftId, file, { reindex, usePageBatches } = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams({ draft_id: draftId });
  if (reindex) params.set("reindex", "true");
  if (usePageBatches) params.set("use_page_batches", "true");
  const res = await extractorApi.post(
    `/readpdf/universal-extract-v2/?${params.toString()}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return res.data; // { job_id, poll_url, pdf_key, file_url, ... }
};

export const getExtractionJobStatus = async (jobId) => {
  const res = await extractorApi.get(`/readpdf/extraction-status/${jobId}/`);
  return res.data; // { status, stage, fields?, extracted_data?, error? }
};

export default extractorApi;
